import { pb, logWriteError, type PortraitRecord } from '@/shared/api'
import type { Character } from '../types/character'

/**
 * Where character portraits live.
 *
 * A collection of its own, publicly readable, and that is the whole reason it is
 * not a file field on `characters`. A file field inherits its record's view rule,
 * which is owner-only, so a shared character opened by somebody with no account
 * would render with a broken image. This is the Supabase public bucket, in the
 * shape PocketBase has for it.
 *
 * One record per character, with the record id set to the character id: the path
 * is therefore stable and deleting a portrait needs no lookup.
 */
export const PORTRAIT_COLLECTION = 'portraits'
const AVATAR_SIZE = 128
const AVATAR_QUALITY = 0.9

/**
 * The collection's id, which PocketBase derives from its name (`pbc_` + crc32),
 * so it is the same on every server. `pb.files.getURL` prefers it over the name.
 */
const PORTRAIT_COLLECTION_ID = 'pbc_1440694835'

/** True when the avatar already lives in our own file store (a hosted URL). */
export function isHostedAvatar(avatar: string): boolean {
  return [PORTRAIT_COLLECTION, PORTRAIT_COLLECTION_ID].some((c) => avatar.includes(`/api/files/${c}/`))
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // Remote sources must be CORS-enabled or the canvas is tainted and toBlob throws.
    // data: URLs are same-origin and need no crossOrigin hint.
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not decode portrait image'))
    img.src = src
  })
}

/** Resize to a centred 128×128 square (cover crop) and encode as JPEG at 90% quality. */
async function resizeAvatar(dataUrl: string): Promise<Blob> {
  const img = await loadImage(dataUrl)
  const canvas = document.createElement('canvas')
  canvas.width = AVATAR_SIZE
  canvas.height = AVATAR_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  // Cover-crop: scale so the shorter side fills the square, then centre.
  const scale = Math.max(AVATAR_SIZE / img.width, AVATAR_SIZE / img.height)
  const w = img.width * scale
  const h = img.height * scale
  ctx.drawImage(img, (AVATAR_SIZE - w) / 2, (AVATAR_SIZE - h) / 2, w, h)
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode portrait'))),
      'image/jpeg',
      AVATAR_QUALITY,
    ),
  )
}

/**
 * Store the portrait and return the record, creating it the first time.
 *
 * Update-then-create rather than a lookup first, because the id is known: one
 * request in the common case, two only on the first upload ever. This is the
 * shape that stands in for `upsert` throughout the app.
 */
async function putPortrait(id: string, userId: string, blob: Blob): Promise<PortraitRecord> {
  const form = new FormData()
  form.append('image', blob, `${id}.jpg`)
  try {
    return await pb.collection(PORTRAIT_COLLECTION).update<PortraitRecord>(id, form)
  } catch (error) {
    if ((error as { status?: number }).status !== 404) throw error
    form.append('id', id)
    form.append('owner', userId)
    return await pb.collection(PORTRAIT_COLLECTION).create<PortraitRecord>(form)
  }
}

/**
 * Keep inline base64 (and other non-hosted) portraits out of the character JSON.
 * When the character's `avatar` isn't already a hosted URL, push a 128×128 /
 * 90%-quality JPEG to the portraits collection and return a copy whose `avatar`
 * is that URL instead. Already hosted URLs (and the empty case) are returned
 * untouched, unless `force` is set: duplicating a character passes `true` so the
 * copy gets its own portrait record at its own id, rather than pointing at the
 * original's, which `remove()` deletes when the original goes away. Any failure
 * logs and falls back to the original character, so a save never silently drops
 * the portrait.
 */
export async function uploadAvatarIfNeeded(
  character: Character,
  userId: string,
  id: string,
  force = false,
): Promise<Character> {
  const avatar = character.avatar
  if (!avatar || (isHostedAvatar(avatar) && !force)) return character
  try {
    const record = await putPortrait(id, userId, await resizeAvatar(avatar))
    // No cache-busting query string, unlike the bucket version: PocketBase
    // suffixes every stored filename, so replacing a portrait changes its URL
    // and a cached copy of the old one cannot mask the new one.
    return { ...character, avatar: pb.files.getURL(record, record.image) }
  } catch (err) {
    console.error('[avatar] could not store portrait', err)
    return character
  }
}

/**
 * Remove a character's hosted portrait. No-op if it never had one.
 *
 * No `userId`, unlike the bucket version, which needed one to build the path
 * `<user>/<character>.jpg`. The record id IS the character id, and the delete
 * rule is what checks ownership.
 */
export async function removeAvatar(id: string): Promise<void> {
  try {
    await pb.collection(PORTRAIT_COLLECTION).delete(id)
  } catch (error) {
    // Nothing to delete is the ordinary case for a character that never had a
    // portrait, so a 404 is silence rather than noise.
    if ((error as { status?: number }).status !== 404) logWriteError('portrait delete')(error)
  }
}
